import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import EquipmentDetailPage from './pages/EquipmentDetailPage';
import EquipmentPage from './pages/EquipmentPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminEquipmentList from './pages/admin/AdminEquipmentList';
import AdminEquipmentForm from './pages/admin/AdminEquipmentForm';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCategoriesList from './pages/admin/AdminCategoriesList';
import AdminCategoryForm from './pages/admin/AdminCategoryForm';
import AdminBookingsList from './pages/admin/AdminBookingsList';
import AdminBookingDetail from './pages/admin/AdminBookingDetail';
import AdminRoleDiscounts from './pages/admin/AdminRoleDiscounts';
import TestNotificationsPage from './pages/admin/TestNotificationsPage';
import { getCurrentUser } from './services/telegram-auth';

// Временные заглушки для страниц
// const Booking = () => <div className="p-8"><h1 className="text-3xl font-bold">Забронировать</h1></div>;

// Компонент для защищенных маршрутов
const ProtectedRoute = ({ redirectPath = '/login' }) => {
  const isAuthenticated = !!getCurrentUser();
  
  if (!isAuthenticated) {
    // Сохраняем текущий путь для перенаправления после авторизации
    localStorage.setItem('authRedirectUrl', window.location.pathname);
    return <Navigate to={redirectPath} replace />;
  }
  
  return <Outlet />;
};

// Создаем QueryClient для React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

function App() {
  // Используем состояние или удаляем если не нужно
  // const [user, setUser] = useState<any>(null);

  useEffect(() => {
    // Проверяем аутентификацию пользователя при загрузке
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Получаем данные аутентифицированного пользователя из localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        // const parsedUser = JSON.parse(userData);
        // setUser(parsedUser);
        // Просто проверяем валидность JSON для проверки данных
        JSON.parse(userData);
      } catch (e) {
        console.error('Ошибка при парсинге данных пользователя:', e);
        localStorage.removeItem('user');
      }
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#333',
              borderRadius: '10px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              padding: '12px 16px',
              fontSize: '0.925rem',
              maxWidth: '400px',
              fontWeight: '500',
            },
            success: {
              style: {
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                border: 'none',
                color: '#065f46',
              },
              icon: '🎉',
            },
            error: {
              style: {
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                border: 'none',
                color: '#991b1b',
              },
              icon: '❌',
            },
          }}
        />

        <Routes>
          <Route path="/" element={<MainLayout />}>
            {/* Общедоступные маршруты */}
            <Route index element={<HomePage />} />
            <Route path="equipment" element={<EquipmentPage />} />
            <Route path="equipment/:id" element={<EquipmentDetailPage />} />
            <Route path="login" element={<LoginPage />} />
            
            {/* Защищенные маршруты, требующие авторизации */}
            <Route element={<ProtectedRoute />}>
              <Route path="profile" element={<ProfilePage />} />
              {/* Маршруты для админ-панели */}
              <Route path="admin" element={<AdminDashboard />} />
              <Route path="admin/equipment" element={<AdminEquipmentList />} />
              <Route path="admin/equipment/new" element={<AdminEquipmentForm />} />
              <Route path="admin/equipment/edit/:id" element={<AdminEquipmentForm />} />
              <Route path="admin/categories" element={<AdminCategoriesList />} />
              <Route path="admin/categories/new" element={<AdminCategoryForm />} />
              <Route path="admin/categories/edit/:id" element={<AdminCategoryForm />} />
              <Route path="admin/bookings" element={<AdminBookingsList />} />
              <Route path="admin/bookings/:id" element={<AdminBookingDetail />} />
              <Route path="admin/discounts" element={<AdminRoleDiscounts />} />
              <Route path="admin/test-notifications" element={<TestNotificationsPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
